import React from "react";
import { Card } from "../Card";
import Select from "react-select";
import { numberWithCommas } from "../../configs/formatCurrency";
import moment from "moment";

const ApplyNew = ({
  onChangeSacco,
  onChangeMember,
  onChangeLoanProduct,
  onLoanDataChange,
  onChangeFees,
  onGenerateLoanAmortization,
  onSubmitLoanData,
  isSubmitting,
  isLoading,
  validationErrors,
  apps,
  members,
  loanProducts,
  role,
  loanData,
  applicableFees,
  amortizationSchedule,
}) => {
  const amortizationMethods = [
    { value: 'fixed_interest', label: 'Fixed Interest' },
    { value: 'reducing_balance', label: 'Reducing Balance' },
    { value: 'straight_line', label: 'Straight Line' }
  ];

  const interestFrequencies = [
    { value: 'annually', label: 'Annually' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const durationUnits = [
    { value: 'weeks', label: 'Weeks' },
    { value: 'months', label: 'Months' },
    { value: 'years', label: 'Years' }
  ];

  const loanProductOptions = loanProducts?.map(product => ({
    value: product._id,
    label: product.name,
    ...product
  }));

  return (
    <>
      <div className="row" style={{ margin: "10px" }}>
        <div className="col-md-8">
          <Card>
            <div className="panel-heading card-title">
              <span><i className="fa fa-money-bill-wave"></i> Loan Application Form</span>
            </div>
            
            <div className="card-body form-container">
              {/* MEMBER SECTION */}
              <div className="section-header">
                <h4><i className="fa fa-building"></i> Member Details</h4>
                <hr />
              </div>
              
              <div className="row">
                {/* Sacco Selection */}
                <div className="col-md-6">
                  <div className="form-group">
                    <label><i className="fa fa-building"></i> Sacco <span className="required">*</span></label>
                    <Select
                      options={apps}
                      onChange={onChangeSacco}
                      className="form-control-select"
                      placeholder="Select Sacco"
                      isLoading={isLoading}
                      value={apps.find(app => app.value === loanData.app_id)}
                    />
                    <span className="text-danger">{validationErrors.app_id}</span>
                  </div>
                </div>

                {/* Member Selection (Admin only) */}
                {role === "admin" && (
                  <div className="col-md-6">
                    <div className="form-group">
                      <label><i className="fa fa-user"></i> Member <span className="required">*</span></label>
                      <Select
                        options={members}
                        onChange={onChangeMember}
                        className="form-control-select"
                        placeholder="Select Member"
                        isLoading={isLoading}
                        value={members.find(member => member.value === loanData.member_id)}
                      />
                      <span className="text-danger">{validationErrors.member_id}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* LOAN PRODUCT DETAILS SECTION */}
              <div className="section-header mt-4">
                <h4><i className="fa fa-money-bill-wave"></i> Loan Product Details</h4>
                <hr />
              </div>
              
              <div className="row">
                {/* Loan Product - Half Width */}
                <div className="col-md-6">
                  <div className="form-group">
                    <label><i className="fa fa-money-bill-wave"></i> Loan Product <span className="required">*</span></label>
                    <Select
                      options={loanProductOptions}
                      onChange={onChangeLoanProduct}
                      className="form-control-select"
                      placeholder="Select Loan Product"
                      value={loanProductOptions.find(product => product.value === loanData.loan_product_id)}
                    />
                    <span className="text-danger">{validationErrors.loan_product_id}</span>
                  </div>
                </div>

                {/* Amortization Method */}
                <div className="col-md-6">
                  <div className="form-group">
                    <label><i className="fa fa-calculator"></i> Amortization Method <span className="required">*</span></label>
                    <Select
                      name="amortization_method"
                      options={amortizationMethods}
                      value={amortizationMethods.find(method => method.value === loanData.amortization_method)}
                      onChange={(e) => onLoanDataChange({ target: { name: 'amortization_method', value: e.value } })}
                      className="form-control-select"
                      placeholder="Select Amortization Method"
                      isClearable={false}
                    />
                    <span className="text-danger">{validationErrors.amortization_method}</span>
                  </div>
                </div>

                {/* Interest Rate and Frequency - Side by Side */}
                <div className="col-md-6">
                  <div className="form-group">
                    <label><i className="fa fa-percentage"></i> Interest Rate <span className="required">*</span></label>
                    <div className="input-group-container">
                      <input
                        name="interest_rate"
                        type="number"
                        className="form-control input-left"
                        value={loanData.interest_rate}
                        onChange={onLoanDataChange}
                        placeholder="Rate (%)"
                      />
                      <Select
                        name="interest_frequency"
                        options={interestFrequencies}
                        value={interestFrequencies.find(frequency => frequency.value === loanData.interest_frequency)}
                        onChange={(e) => onLoanDataChange({ target: { name: 'interest_frequency', value: e.value } })}
                        className="input-right"
                      />
                    </div>
                    <span className="text-danger">{validationErrors.interest_rate}</span>
                    <span className="text-danger">{validationErrors.interest_frequency}</span>
                  </div>
                </div>

                {/* Duration and Unit - Side by Side */}
                <div className="col-md-6">
                  <div className="form-group">
                    <label><i className="fa fa-clock"></i> Loan Duration <span className="required">*</span></label>
                    <div className="input-group-container">
                      <input
                        name="duration"
                        type="number"
                        className="form-control input-left"
                        value={loanData.duration}
                        onChange={onLoanDataChange}
                        placeholder="Duration"
                      />
                      <Select
                        name="duration_unit"
                        options={durationUnits}
                        value={durationUnits.find(unit => unit.value === loanData.duration_unit)}
                        onChange={(e) => onLoanDataChange({ target: { name: 'duration_unit', value: e.value } })}
                        className="input-right"
                      />          
                    </div>
                    <span className="text-danger">{validationErrors.duration}</span>
                    <span className="text-danger">{validationErrors.duration_unit}</span>
                  </div>
                </div>
              </div>

              {/* LOAN DETAILS SECTION */}
              <div className="section-header mt-4">
                <h4><i className="fa fa-info-circle"></i> Loan Details</h4>
                <hr />
              </div>
              
              <div className="row">
                {/* Principal Amount */}
                <div className="col-md-6">
                  <div className="form-group">
                    <label><i className="fa fa-money-bill-wave"></i> Principal Amount <span className="required">*</span></label>
                    <input
                      name="principal_amount"
                      type="number"
                      className="form-control"
                      value={loanData.principal_amount}
                      onChange={onLoanDataChange}
                      placeholder="Principal Amount"
                    />
                    <span className="text-danger">{validationErrors.principal_amount}</span>
                  </div>
                </div>

                {/* Pre-onboarding Payment */}
                <div className="col-md-6">
                  <div className="form-group">
                    <label><i className="fa fa-money-bill-wave"></i> Pre-onboarding Payment</label>
                    <input
                      name="pre_onboarding_payment"
                      type="number"
                      className="form-control"
                      value={loanData.pre_onboarding_payment}
                      onChange={onLoanDataChange}
                    />
                  </div>
                </div>

                {/* Start Date - Moved here */}
                <div className="col-md-6">
                  <div className="form-group">
                    <label><i className="fa fa-calendar"></i> Start Date <span className="required">*</span></label>
                    <input
                      type="date"
                      name="start_date"
                      className="form-control"
                      value={loanData.start_date}
                      onChange={onLoanDataChange}
                    />
                    <span className="text-danger">{validationErrors.start_date}</span>
                  </div>
                </div>
              </div>

              {/* FEES SECTION */}
              <div className="section-header mt-4">
                <h4><i className="fa fa-receipt"></i> Fees</h4>
                <hr />
              </div>
              
              <div className="row">
                {Object.keys(applicableFees || {}).map((fee, i) => (
                  <div className="col-md-4" key={i}>
                    <div className="form-group">
                      <label style={{ textTransform: "capitalize" }}>
                        <i className="fa fa-money-bill-wave"></i> {fee.replace(/_/g, ' ')}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={loanData?.fees[fee]}
                        onChange={(e) => onChangeFees(fee, e)}
                        placeholder="Enter amount (optional)"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="row mt-4">
                <div className="col-md-6">
                  <div className="d-flex justify-content-center">
                    <button 
                      className="btn btn-primary action-button" 
                      onClick={onGenerateLoanAmortization}
                      disabled={isSubmitting}
                    >
                      <i className={`fa ${isSubmitting ? "fa-spinner fa-spin" : "fa-calculator"}`}></i> Generate
                    </button>
                  </div>
                </div>
                {amortizationSchedule && (
                  <div className="col-md-6">
                    <div className="d-flex justify-content-center">
                      <button
                        className="btn btn-success action-button"
                      onClick={onSubmitLoanData}
                        disabled={isSubmitting}
                      >
                        <i className="fa fa-paper-plane"></i> {isSubmitting ? "Submitting..." : "Apply Loan"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* SUMMARY CARD */}
        {loanData.principal_amount >= 0 && (
          <>
            <div className="col-md-4">
              <Card>
                <div className="panel-heading card-title">
                  <span><i className="fa fa-file-invoice"></i> LOAN SUMMARY</span>
                </div>

                <div className="panel-body summary-container">
                  {/* LOAN DETAILS */}
                  <div className="summary-section">
                    <h5><i className="fa fa-info-circle"></i> Loan Details</h5>
                    <div className="summary-item">
                      <div className="summary-label">
                        <i className="fa fa-money-bill-wave"></i> <span>Principal</span>
                      </div>
                      <div className="summary-value">
                        KES {numberWithCommas(Math.ceil(loanData.principal_amount))}
                      </div>
                    </div>
                    
                    <div className="summary-item">
                      <div className="summary-label">
                        <i className="fa fa-percentage"></i> <span>Interest Rate</span>
                      </div>
                      <div className="summary-value">
                        {loanData.interest_rate}% ({loanData.interest_frequency})
                      </div>
                    </div>

                    <div className="summary-item">
                      <div className="summary-label">
                        <i className="fa fa-clock"></i> <span>Duration</span>
                      </div>
                      <div className="summary-value">
                        {loanData.duration} ({loanData.duration_unit})
                      </div>
                    </div>
                  </div>

                  {/* COMPUTED TOTALS */}
                  {amortizationSchedule && (
                    <div className="summary-section c">
                      <h5><i className="fa fa-calculator"></i> Computed Totals</h5>
                      <div className="summary-item">
                        <div className="summary-label">
                          <i className="fa fa-coins"></i> <span>Monthly Payment</span>
                        </div>
                        <div className="summary-value">
                          KES {numberWithCommas(Math.ceil(amortizationSchedule?.installment_amount))}
                        </div>
                      </div>

                      <div className="summary-item">
                        <div className="summary-label">
                          <i className="fa fa-percentage"></i> <span>Total Interest</span>
                        </div>
                        <div className="summary-value">
                          KES {numberWithCommas(Math.ceil(
                            amortizationSchedule.total_interest
                          ))}
                        </div>
                      </div>

                      <div className="summary-item">
                        <div className="summary-label">
                          <i className="fa fa-money-bill-wave"></i> <span>Total Amount</span>
                        </div>
                        <div className="summary-value">
                          KES {numberWithCommas(Math.ceil(
                            amortizationSchedule?.total_amount
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* AMORTIZATION SCHEDULE */}
            <div className="col-md-12 mt-4">
              <Card>
                <div className="panel-heading card-title">
                  <span><i className="fa fa-calendar-alt"></i> AMORTIZATION SCHEDULE</span>
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered table-striped">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th style={{textAlign: "center"}}>Due Date</th>
                        <th style={{textAlign: "right"}}>Beginning Balance</th>
                        <th style={{textAlign: "right"}}>Principal</th>
                        <th style={{textAlign: "right"}}>Interest</th>
                        <th style={{textAlign: "right"}}>Total</th>
                        <th style={{textAlign: "right"}}>Ending Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {amortizationSchedule?.amortization_schedule_entries?.length > 0 && (
                        <>
                          {amortizationSchedule.amortization_schedule_entries.map((item, i) => (
                            <tr key={i}>
                              <td>{item.installment_number}</td>
                              <td style={{textAlign: "center"}}>{moment(item.due_date).format('DD/MM/YYYY')}</td>
                              <td style={{textAlign: "right"}}>{numberWithCommas(Math.ceil(item.beginning_balance))}</td>
                              <td style={{textAlign: "right"}}>{numberWithCommas(Math.ceil(item.principal))}</td>
                              <td style={{textAlign: "right"}}>{numberWithCommas(Math.ceil(item.interest))}</td>
                              <td style={{textAlign: "right"}}>{numberWithCommas(Math.ceil(item.total))}</td>
                              <td style={{textAlign: "right"}}>{numberWithCommas(Math.ceil(item.ending_balance))}</td>
                            </tr>
                          ))}
                          <tr className="totals-row">
                            <td colSpan={2}><strong>TOTALS</strong></td>
                            <td style={{textAlign: "right"}}>-</td>
                            <td style={{textAlign: "right"}}>
                              <strong>
                                {numberWithCommas(Math.ceil(
                                  amortizationSchedule.amortization_schedule_entries.reduce((sum, item) => sum + item.principal, 0)
                                ))}
                              </strong>
                            </td>
                            <td style={{textAlign: "right"}}>
                              <strong>
                                {numberWithCommas(Math.ceil(
                                  amortizationSchedule.amortization_schedule_entries.reduce((sum, item) => sum + item.interest, 0)
                                ))}
                              </strong>
                            </td>
                            <td style={{textAlign: "right"}}>
                              <strong>
                                {numberWithCommas(Math.ceil(
                                  amortizationSchedule.amortization_schedule_entries.reduce((sum, item) => sum + item.total, 0)
                                ))}
                              </strong>
                            </td>
                            <td style={{textAlign: "right"}}>-</td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </>
        )}
      </div>

      {/* Keep existing styles */}
      <style jsx>{`
        .summary-container {
          padding: 15px;
          background-color: #f9f9f9;
          border-radius: 4px;
        }
        .summary-section {
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 1px dashed #ddd;
        }
        .summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 12px;
          border-bottom: 1px solid #eee;
        }
        .summary-label {
          display: flex;
          align-items: center;
          color: #555;
          font-size: 13px;
        }
        .summary-label i {
          color: #F79222;
          margin-right: 8px;
          width: 16px;
          text-align: center;
        }
        .summary-value {
          font-weight: 600;
          color: #333;
        }
        .table-responsive {
          overflow-x: auto;
        }
        .table-bordered {
          border: 1px solid #ddd;
        }
        .table-striped tbody tr:nth-of-type(odd) {
          background-color: rgba(247, 146, 34, 0.05);
        }

        .form-container {
          padding: 25px;
        }

        .form-group {
          margin-bottom: 20px;
          padding: 0 10px;
        }

        .section-header {
          padding: 0 10px;
        }

        .action-button {
          max-width: 200px;
          width: 100%;
          padding: 10px 20px;
          font-size: 16px;
          font-weight: 600;
          margin: 10px;
        }

        .btn-primary.action-button {
          background-color: #F79222;
          border-color: #F79222;
        }

        .btn-primary.action-button:hover {
          background-color: #e07b10;
          border-color: #e07b10;
        }

        .btn-success.action-button {
          background-color: #28a745;
          border-color: #28a745;
        }

        .btn-success.action-button:hover {
          background-color: #218838;
          border-color: #218838;
        }

        .btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        .form-control, .form-control-select {
          padding: 8px 12px;
          height: auto;
        }

        .row {
          margin-left: -10px;
          margin-right: -10px;
        }

        .col-md-4, .col-md-6, .col-md-12 {
          padding-left: 10px;
          padding-right: 10px;
        }

        .card-title {
          padding: 15px 25px;
          margin-bottom: 0;
          border-bottom: 2px solid #F9394C;
        }

        .section-header h4 {
          margin-top: 10px;
          margin-bottom: 15px;
        }

        hr {
          margin-top: 10px;
          margin-bottom: 20px;
          border-color: #eee;
        }

        .input-group-container {
          display: flex;
          gap: 10px;
        }
        .input-left {
          width: 50%;
          border-radius: 4px;
        }
        .input-right {
          width: 50%;
        }
        .form-control-select {
          padding: 0;
          height: auto;
        }
        
        /* Override react-select default styles */
        .form-control-select > div {
          border-radius: 4px;
          min-height: 38px;
        }
        
        .form-control-select > div:first-of-type {
          border-color: #ced4da;
        }

        .input-right > div {
          border-radius: 4px;
          min-height: 38px;
        }
        
        .input-right > div:first-of-type {
          border-color: #ced4da;
        }

        .form-group label {
          margin-bottom: 8px;
          display: block;
        }

        .totals-row {
          background-color: #f8f9fa;
          border-top: 2px solid #dee2e6;
        }

        .totals-row td {
          font-weight: 500;
          color: #212529;
        }

        .computed-totals {
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px dashed #dee2e6;
        }

        .computed-totals h5 {
          color: #F7941D;
        }

        .computed-totals .summary-item {
          background-color: #f8f9fa;
          padding: 12px;
          border-radius: 6px;
          margin-bottom: 10px;
        }

        .computed-totals .summary-value.highlight {
          color: #F7941D;
          font-weight: 600;
          font-size: 1.1em;
        }

        .computed-totals .fa-calculator {
          color: #F7941D;
        }
      `}</style>
    </>
  );
};

export default ApplyNew;
